import { mapRoomAvailability } from '@/services/hours-map.js';

import { parseFormFromBackend } from '../misc/parsePayload.js';

const ensureNotPastDate = (date) => {
  if (!date) {
    return null;
  }

  const endOfDayForDate = window.dayjs(date, 'DD/MM/YYYY').endOf('day');
  const now = window.dayjs();

  if (endOfDayForDate.isBefore(now)) {
    return null;
  }

  return date;
};

const mutations = {
  setDateAndPeopleForm(state, form) {
    state.form.dateAndPeople = {
      eventType: form.eventType.value,
      date: ensureNotPastDate(form.date.value),
      people: form.people.value,
      timeFrom: form.timeFrom.value,
      timeTo: form.timeTo.value,
      layout: form.layout.value,
      flexibleOnTime: state.roomSetupAllowsOP && state.requestToBookPath === true ? false : form.flexibleOnTime.value,
    };
  },

  setDateAndPeople(state, { date, people, timeFrom, timeTo, layout }) {
    state.form.dateAndPeople = {
      ...state.form.dateAndPeople,
      date: ensureNotPastDate(date),
      people,
    };

    if (timeFrom) {
      state.form.dateAndPeople.timeFrom = timeFrom;
    }

    if (timeTo) {
      state.form.dateAndPeople.timeTo = timeTo;
    }

    if (layout) {
      state.form.dateAndPeople.layout = layout;
    }
  },

  setLayout(state, layout) {
    state.form.dateAndPeople.layout = layout;
  },
  setCatering(state) {
    state.form.messageAndCatering.catering.requireCatering = true;
    state.form.messageAndCatering.catering.ownCatering = false;
  },
  setOwnCatering(state) {
    state.form.messageAndCatering.catering.requireCatering = false;
    state.form.messageAndCatering.catering.ownCatering = true;
  },

  setMessageAndCateringForm(state, form, isExtendedCateringFlow = false) {
    const { catering, extendedCatering } = form;

    // drinksReception is only option available in both flows: normal catering and extended catering flow
    let drinksReception = catering.drinksReception.value;
    if (isExtendedCateringFlow) {
      drinksReception = extendedCatering.drinksReception.value;
    }

    state.form.messageAndCatering = {
      message: form.message.value,
      catering: {
        requireCatering: catering.requireCatering.value,
        ownCatering: catering.ownCatering.value,
        breakfast: catering.breakfast.value,
        teaAndCoffee: catering.teaAndCoffee.value,
        lunch: catering.lunch.value,
        dinner: catering.dinner.value,
        drinksReception,
        other: catering.other.value,
      },
      extendedCatering: {
        seatedMeal: extendedCatering.seatedMeal.value,
        buffet: extendedCatering.buffet.value,
        canapes: extendedCatering.canapes.value,
        drinksReception,
        openBar: extendedCatering.openBar.value,
      },
    };
  },

  setUserInfoForm(state, form) {
    state.form.userInfo = {
      fullName: form.fullName.value,
      email: form.email.value,
      password: form.password.value,
      phone: form.phone.value,
      marketingConsent: form.marketingConsent.value,
    };
  },

  setEventTypes(state, { tags }) {
    state.eventTypes = tags.map(({ value, data }) => ({
      label: value,
      value: data.key,
      id: data.id,
    }));
  },

  setUserLoggedIn(state) {
    state.user.isLoggedIn = true;
  },
  setUserSuccessfullySignUp(state) {
    state.user.isSuccesfullySignUp = true;
  },
  setUserSuccessfullyLoggedIn(state) {
    state.user.isSuccesfullyLoggedIn = true;
  },

  setLastEnquiry(state, { date, url }) {
    state.user.lastEnquiry = {
      date,
      url,
    };
  },

  setRoomAvailability(state, payload) {
    // partiallyBookedDays, fullyBookedDays, closedDaysOfWeek, openingHours
    state.room.availability = {
      ...state.room.availability,
      ...mapRoomAvailability(payload),
    };
  },

  setRoomCatering(state, { availableByVenue, availableExternal }) {
    state.room.catering = {
      availableByVenue,
      availableExternal,
    };
  },

  enableCaptcha(state) {
    state.captcha.isEnabled = true;
  },

  disableCaptcha(state) {
    state.captcha.isEnabled = false;
  },

  setCaptchaToken(state, value) {
    state.captcha.token = value;
  },

  setLastForm(state, lastForm) {
    const parsedForm = parseFormFromBackend(lastForm);
    state.form.dateAndPeople = {
      ...parsedForm.dateAndPeople,
      date: ensureNotPastDate(parsedForm.dateAndPeople.date),
    };

    state.form.messageAndCatering = parsedForm.messageAndCatering;

    if (state.roomSetupAllowsOP) {
      state.form.dateAndPeople.flexibleOnTime = false;
      state.form.messageAndCatering.message = '';
      state.form.messageAndCatering.catering = {
        requireCatering: false,
        ownCatering: false,
        breakfast: false,
        teaAndCoffee: false,
        lunch: false,
        dinner: false,
        drinksReception: false,
        other: false,
      };
    }

    if (!state.user.isLoggedIn) {
      state.form.userInfo = parsedForm.userInfo;
    }
  },

  setBigDesktop(state, value) {
    state.isBigDesktop = value;
  },

  setLoading(state, { request, value }) {
    state.isLoading[request] = value;
  },

  setVenueResponse(state, { rate, time }) {
    state.venue.responseRate = rate;
    state.venue.responseTime = time;
  },

  hideSuspensionWarning(state) {
    state.showSuspensionWarning = false;
  },

  setPricingLoading(state, isLoading) {
    state.pricing.loading = isLoading;
  },
  setMinimumPricing(state, data) {
    state.minimumPricing = data;
  },
  setPaymentDetails(state, data) {
    state.pricing.paymentDetails = data;
  },
  setRoomSetupAllowsOP(state, value) {
    state.roomSetupAllowsOP = value;
  },
  setLeadHubOffer(state, value) {
    state.leadHubOffer = value;
  },
  setNoticeHours(state, value) {
    state.noticeHours = value;
  },
  setRequestToBookPath(state, value) {
    state.requestToBookPath = value;
  },
  setRequestToBookMobilePath(state, value) {
    state.requestToBookMobilePath = value;
  },
  setStep(state, value) {
    state.step = value;
  },
  setSocialAuthMethod(state, value) {
    state.socialAuthMethod = value;
  },
  setAnalyticsClientId(state, value) {
    state.analyticsClientId = value;
  },
  setIsEnquiryFormModalOpen(state, value) {
    state.isEnquiryFormModalOpen = value;
  },
  setEnquirySentModalConfig(state, value) {
    state.enquirySentModalConfig = value;
  },
  setManagerLastActivityOnline(state, value) {
    state.managerLastActivityOnline = value;
  },
  setEnquiryFormReady(state, value) {
    state.enquiryFormReady = value;
  },
};

export default mutations;
